import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

const DEFAULT_MESSAGE = 'Leave this report and discard your unsaved financial review changes?';
const historyIndex = () => Number.isSafeInteger(window.history.state?.idx) ? window.history.state.idx : null;

/**
 * BrowserRouter does not expose the data router's useBlocker API. Guard its
 * navigator calls and intercept indexed POPs before BrowserRouter's listener.
 * A declined POP is reversed with go(delta), preserving both history entries.
 * No report content is written to browser storage or history state.
 *
 * Router-owned entries have an idx. Foreign code must not call pushState or
 * replaceState directly: React Router itself cannot track those entries.
 * Full document navigation is protected by beforeunload instead.
 */
export default function useFinancialReviewNavigationGuard(enabled, message = DEFAULT_MESSAGE) {
  const navigation = useContext(UNSAFE_NavigationContext);
  const latest = useRef({ enabled, message });
  latest.current = { enabled, message };
  const navigator = navigation?.navigator;

  useEffect(() => {
    const confirm = () => !latest.current.enabled || window.confirm(latest.current.message);
    const beforeUnload = event => {
      if (!latest.current.enabled) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    if (!navigator) return () => window.removeEventListener('beforeunload', beforeUnload);

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;
    // MemoryRouter exposes its index; it has no browser POP event to intercept.
    const memoryHistory = Number.isSafeInteger(navigator.index);
    let currentIndex = historyIndex();
    let restoringIndex = null;
    const navigate = method => function (...args) {
      if (restoringIndex !== null || !confirm()) return undefined;
      const result = method.apply(navigator, args);
      currentIndex = historyIndex();
      return result;
    };
    const guardedPush = navigate(originalPush);
    const guardedReplace = navigate(originalReplace);
    const guardedGo = memoryHistory ? navigate(originalGo) : originalGo;
    navigator.push = guardedPush;
    navigator.replace = guardedReplace;
    if (memoryHistory) navigator.go = guardedGo;

    const popState = event => {
      const nextIndex = historyIndex();
      if (restoringIndex !== null) {
        // React Router never saw the rejected destination, so it must not see
        // the restoration either. Keep its location and private index intact.
        event.stopImmediatePropagation();
        if (nextIndex === restoringIndex) restoringIndex = null;
        else if (nextIndex !== null) window.history.go(restoringIndex - nextIndex);
        return;
      }
      if (nextIndex === currentIndex || nextIndex === null || currentIndex === null || confirm()) {
        currentIndex = nextIndex;
        return;
      }
      event.stopImmediatePropagation();
      restoringIndex = currentIndex;
      window.history.go(currentIndex - nextIndex);
    };
    if (!memoryHistory) window.addEventListener('popstate', popState, true);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      if (!memoryHistory) window.removeEventListener('popstate', popState, true);
      // A later guard may own these methods; do not overwrite its wrapper.
      if (navigator.push === guardedPush) navigator.push = originalPush;
      if (navigator.replace === guardedReplace) navigator.replace = originalReplace;
      if (memoryHistory && navigator.go === guardedGo) navigator.go = originalGo;
    };
  }, [navigator]);
}
